import React, { useState, useEffect, useRef } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  Line,
  ZoomableGroup
} from 'react-simple-maps';
import { 
  Globe, 
  MapPin, 
  TrendingUp, 
  TrendingDown, 
  Minus,
  RefreshCw,
  Building2,
  AlertCircle,
  Info,
  Factory,
  Navigation
} from 'lucide-react';
import { colors } from '../../config/colors';
import { gdeltService } from '../../services/gdeltService';
import { createCountrySentiment } from '../../models/gdeltModels';
import { getCoordinatesForAddress } from '../../utils/geocoding';
import LoadingSpinner from '../UI/LoadingSpinner';
import MapShimmer from './MapShimmer';
import { useLocale } from '../../contexts/LocaleContext';
import toast from 'react-hot-toast';

// World map topojson URL - using CDN with reliable world-110m atlas
const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

// Dark map theme (design-system map tokens — gray scale is inverted in dark UI)
const MAP_DARK_OCEAN = colors.map.ocean;
const MAP_DARK_LAND = colors.map.land;
const MAP_DARK_STROKE = colors.map.stroke;
const MAP_DARK_LAND_HOVER = colors.map.landHover;

/**
 * InteractiveWorldMap - SVG-based world map using react-simple-maps
 * Shows countries colored by GDELT sentiment, company markers, and supply chain routes
 * compact: embed mode (no header/legend, dark map, whole world)
 * refreshTrigger: when incremented by parent, triggers data refresh
 */
const InteractiveWorldMap = ({
  watchlistItems = [],
  riskAnalyses = {},
  compact = false,
  refreshTrigger,
  onLoadingComplete,
  selectedCompanyId,
  selectedRouteKey,
  onSelectCompanyLocation,
  onSelectRoute,
}) => {
  const { t } = useLocale();
  const [loading, setLoading] = useState(!compact);
  const [countrySentiments, setCountrySentiments] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [hoveredCountry, setHoveredCountry] = useState(null);
  const [hoveredMarker, setHoveredMarker] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [companyLocations, setCompanyLocations] = useState([]);
  const [routeLines, setRouteLines] = useState([]);
  const refreshTriggerRef = useRef(refreshTrigger);
  const onLoadingCompleteCalledRef = useRef(false);

  useEffect(() => {
    if (compact) {
      setLoading(false);
      fetchCountrySentiments();
    } else {
      fetchCountrySentiments();
    }
  }, []);

  useEffect(() => {
    if (typeof refreshTrigger === 'number' && refreshTrigger > 0 && refreshTrigger !== refreshTriggerRef.current) {
      refreshTriggerRef.current = refreshTrigger;
      handleRefresh();
    }
  }, [refreshTrigger]);

  useEffect(() => {
    if (!loading && !onLoadingCompleteCalledRef.current && typeof onLoadingComplete === 'function') {
      onLoadingCompleteCalledRef.current = true;
      onLoadingComplete();
    }
  }, [loading, onLoadingComplete]);

  useEffect(() => {
    if (watchlistItems.length > 0) {
      processCompanyLocations();
      processRoutes();
    } else {
      setCompanyLocations([]);
      setRouteLines([]);
    }
  }, [watchlistItems, riskAnalyses]);

  const fetchCountrySentiments = async () => {
    try {
      setLoading(true);
      const result = await gdeltService.getAllCountriesSentiment();
      
      if (result.success && result.data.length > 0) {
        const sentiments = result.data.map(item => createCountrySentiment(item));
        setCountrySentiments(sentiments);
        setLastUpdated(new Date());
      } else {
        setCountrySentiments([]);
        setLastUpdated(new Date());
      }
    } catch (error) {
      setCountrySentiments([]);
      setLastUpdated(new Date());
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      const result = await gdeltService.refreshData();
      
      if (result.success) {
        toast.success('Data refreshed successfully');
        await fetchCountrySentiments();
      } else {
        toast.error(result.message || 'Failed to refresh data');
      }
    } catch (error) {
      toast.error('Failed to refresh data');
    } finally {
      setRefreshing(false);
    }
  };

  // Normalize address/route from context (DTO or API snake_case)
  const getAddressId = (addr) => addr?.addressId ?? addr?.address_id ?? '';
  const getRouteStartId = (r) => r?.startPointAddressId ?? r?.start_point_address_id ?? '';
  const getRouteEndId = (r) => r?.endPointAddressId ?? r?.end_point_address_id ?? '';

  // Process company locations from watchlist addresses (one marker per address; geocoding supports DTO and snake_case)
  // Use allAddressesForRouteDisplay when present so inter-company route endpoints are included
  const processCompanyLocations = async () => {
    const locations = [];
    const addresses = (item) => item.userCompanyInfo?.allAddressesForRouteDisplay ?? item.userCompanyInfo?.userAddresses ?? item.userCompanyInfo?.user_addresses ?? [];

    for (const item of watchlistItems) {
      const cid = item.companyId ?? item.company_id ?? '';
      const addrs = addresses(item);
      if (!Array.isArray(addrs) || addrs.length === 0) continue;

      for (const address of addrs) {
        const addressStr = address?.getFullAddress?.() ?? (
          [
            address?.streetAddress ?? address?.street_address,
            address?.city,
            address?.stateProvince ?? address?.state_province,
            address?.postalCode ?? address?.postal_code,
            address?.country,
          ].filter(Boolean).join(', ') || address?.country || ''
        );
        if (!addressStr) continue;
        const coords = await getCoordinatesForAddress(address);
        if (coords && coords.lat !== 0 && coords.lng !== 0) {
          const riskData = riskAnalyses[cid];
          locations.push({
            companyId: cid,
            address,
            priority: item.priority,
            coords,
            riskScore: riskData?.overallRiskScore ?? 0,
          });
        }
      }
    }

    setCompanyLocations(locations);
  };

  // Process routes from watchlist (match by address IDs)
  // Build a global address map from ALL items so inter-company routes resolve (start in A, end in B)
  const processRoutes = async () => {
    const lines = [];
    const addrsList = (item) => item.userCompanyInfo?.allAddressesForRouteDisplay ?? item.userCompanyInfo?.userAddresses ?? item.userCompanyInfo?.user_addresses ?? [];
    const routesList = (item) => item.userCompanyInfo?.routes ?? [];

    const globalAddressMap = new Map();
    for (const item of watchlistItems) {
      const addrs = addrsList(item);
      if (Array.isArray(addrs)) {
        for (const addr of addrs) {
          const id = getAddressId(addr);
          if (id) {
            const key = String(id);
            if (!globalAddressMap.has(key)) globalAddressMap.set(key, addr);
          }
        }
      }
    }

    for (const item of watchlistItems) {
      const cid = item.companyId ?? item.company_id ?? '';
      const routeList = routesList(item);
      if (!Array.isArray(routeList) || routeList.length === 0) continue;

      for (const route of routeList) {
        const startId = getRouteStartId(route);
        const endId = getRouteEndId(route);
        const startAddr = startId ? globalAddressMap.get(String(startId)) : null;
        const endAddr = endId ? globalAddressMap.get(String(endId)) : null;
        if (!startAddr || !endAddr) continue;

        const startCoords = await getCoordinatesForAddress(startAddr);
        const endCoords = await getCoordinatesForAddress(endAddr);

        if (
          startCoords && endCoords &&
          startCoords.lat !== 0 && startCoords.lng !== 0 &&
          endCoords.lat !== 0 && endCoords.lng !== 0
        ) {
          const rid = route.routeId ?? route.route_id;
          lines.push({
            routeId: rid,
            routeKey: `${cid}|${rid}`,
            routeName: route.routeName ?? route.route_name,
            from: startCoords,
            to: endCoords,
            isActive: route.isActive !== false && route.is_active !== false,
            companyId: cid,
          });
        }
      }
    }

    setRouteLines(lines);
  };

  // Get sentiment for a country
  const getSentimentForCountry = (countryName) => {
    return countrySentiments.find(s => s.Country === countryName);
  };

  // Get color based on sentiment tone
  const getSentimentColor = (tone) => {
    if (tone > 5) return colors.success[500];
    if (tone > 2) return colors.success[400];
    if (tone >= -2) return colors.warning[500];
    if (tone >= -5) return colors.danger[400];
    return colors.danger[600];
  };

  // Get color for country fill (compact: highlight risk/sentiment with color on dark map)
  const getCountryFillColor = (geo) => {
    const countryName = geo.properties.name;
    const sentiment = getSentimentForCountry(countryName);
    if (sentiment) {
      const tone = sentiment.Tone;
      if (compact) {
        // Dark map: use more saturated colors so risk/sentiment stands out
        if (tone > 5) return colors.success[500];
        if (tone > 2) return colors.success[400];
        if (tone >= -2) return colors.warning[500];
        if (tone >= -5) return colors.danger[400];
        return colors.danger[600];
      }
      if (tone > 5) return colors.success[200];
      if (tone > 2) return colors.success[100];
      if (tone >= -2) return colors.warning[100];
      if (tone >= -5) return colors.danger[100];
      return colors.danger[200];
    }
    return compact ? MAP_DARK_LAND : colors.gray[100];
  };

  // Get marker color based on risk score
  const getMarkerColor = (riskScore) => {
    if (riskScore >= 70) return colors.danger[500];
    if (riskScore >= 50) return colors.warning[500];
    if (riskScore >= 30) return colors.info[500];
    return colors.success[500];
  };

  // Get companies in a country
  const getCompaniesInCountry = (countryName) => {
    return companyLocations.filter(loc => loc.address.country === countryName);
  };

  if (loading) {
    if (compact) {
      return <MapShimmer className="h-full" style={{ minHeight: 320 }} />;
    }
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className={compact ? 'h-full flex flex-col' : 'space-y-6'}>
      {!compact && (
        <>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: colors.primary[100] }}
          >
            <Globe className="h-6 w-6" style={{ color: colors.primary[600] }} />
          </div>
          <div>
            <h3 className="text-xl font-bold" style={{ color: colors.text.primary }}>
              Global Sentiment & Company Locations
            </h3>
            <p className="text-sm" style={{ color: colors.text.secondary }}>
              {countrySentiments.length} countries monitored • {companyLocations.length} locations • {routeLines.length} routes
            </p>
          </div>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="px-4 py-2 rounded-lg font-medium text-sm flex items-center space-x-2 transition-all hover:shadow-md disabled:opacity-50"
          style={{
            backgroundColor: colors.brand[500],
            color: 'white'
          }}
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          <span>{refreshing ? 'Refreshing...' : 'Refresh Data'}</span>
        </button>
      </div>

      {/* Legend */}
      <div 
        className="p-4 rounded-xl"
        style={{ backgroundColor: colors.gray[50], borderColor: colors.border.light, border: '1px solid' }}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Sentiment Legend */}
          <div>
            <h4 className="font-semibold text-sm mb-2 flex items-center space-x-2" style={{ color: colors.text.primary }}>
              <Info className="h-4 w-4" />
              <span>Sentiment Scale</span>
            </h4>
            <div className="space-y-1">
              {[
                { label: 'Very Positive', color: colors.success[200] },
                { label: 'Positive', color: colors.success[100] },
                { label: 'Neutral', color: colors.warning[100] },
                { label: 'Negative', color: colors.danger[100] },
                { label: 'Very Negative', color: colors.danger[200] }
              ].map(item => (
                <div key={item.label} className="flex items-center space-x-2">
                  <div 
                    className="w-4 h-4 rounded"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-xs" style={{ color: colors.text.secondary }}>
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Risk Legend */}
          <div>
            <h4 className="font-semibold text-sm mb-2 flex items-center space-x-2" style={{ color: colors.text.primary }}>
              <AlertCircle className="h-4 w-4" />
              <span>Risk Score</span>
            </h4>
            <div className="space-y-1">
              {[
                { label: 'High Risk (70+)', color: colors.danger[500] },
                { label: 'Medium Risk (50-69)', color: colors.warning[500] },
                { label: 'Low Risk (30-49)', color: colors.info[500] },
                { label: 'Minimal Risk (<30)', color: colors.success[500] }
              ].map(item => (
                <div key={item.label} className="flex items-center space-x-2">
                  <MapPin className="h-4 w-4" style={{ color: item.color }} />
                  <span className="text-xs" style={{ color: colors.text.secondary }}>
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Route Legend */}
          <div>
            <h4 className="font-semibold text-sm mb-2 flex items-center space-x-2" style={{ color: colors.text.primary }}>
              <Navigation className="h-4 w-4" />
              <span>Supply Chain Routes</span>
            </h4>
            <div className="space-y-1">
              <div className="flex items-center space-x-2">
                <div 
                  className="w-4 h-1"
                  style={{ backgroundColor: colors.brand[500] }}
                />
                <span className="text-xs" style={{ color: colors.text.secondary }}>
                  Active Route
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <div 
                  className="w-4 h-1"
                  style={{ backgroundColor: colors.gray[400] }}
                />
                <span className="text-xs" style={{ color: colors.text.secondary }}>
                  Inactive Route
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
        </>
      )}

      {/* Interactive Map */}
      <div 
        className={compact ? 'flex-1 min-h-0 rounded-lg overflow-hidden relative' : 'rounded-xl overflow-hidden border-2 relative'}
        style={{ 
          ...(compact ? { backgroundColor: MAP_DARK_OCEAN } : { borderColor: colors.border.light, backgroundColor: colors.background.card }),
          height: compact ? '100%' : '600px'
        }}
      >
        <ComposableMap
          projection="geoMercator"
          projectionConfig={{
            scale: 140,
            center: [0, 20]
          }}
          style={{
            width: '100%',
            height: '100%',
            backgroundColor: MAP_DARK_OCEAN,
          }}
        >
          <ZoomableGroup>
            <Geographies geography={GEO_URL}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const isHovered = hoveredCountry === geo.properties.name;
                  const sentiment = getSentimentForCountry(geo.properties.name);
                  
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      onMouseEnter={() => setHoveredCountry(geo.properties.name)}
                      onMouseLeave={() => setHoveredCountry(null)}
                      onClick={() => setSelectedCountry(sentiment || { Country: geo.properties.name })}
                      style={{
                        default: {
                          fill: getCountryFillColor(geo),
                          stroke: compact ? MAP_DARK_STROKE : colors.border.light,
                          strokeWidth: 0.5,
                          outline: 'none'
                        },
                        hover: {
                          fill: sentiment ? getSentimentColor(sentiment.Tone) : (compact ? MAP_DARK_LAND_HOVER : colors.primary[200]),
                          stroke: compact ? colors.border.medium : colors.brand[500],
                          strokeWidth: 1.5,
                          outline: 'none',
                          cursor: 'pointer'
                        },
                        pressed: {
                          fill: sentiment ? getSentimentColor(sentiment.Tone) : (compact ? MAP_DARK_LAND_HOVER : colors.brand[400]),
                          stroke: compact ? colors.border.medium : colors.primary[600],
                          strokeWidth: 1.5,
                          outline: 'none'
                        }
                      }}
                    />
                  );
                })
              }
            </Geographies>

            {/* Route Lines - highlighted with color (active vs inactive); clickable when onSelectRoute provided */}
            {routeLines.map((route, idx) => {
              const isSelected = selectedRouteKey === (route.routeKey || `${route.companyId}|${route.routeId}`);
              const strokeColor = isSelected
                ? colors.warning[500]
                : route.isActive
                  ? (compact ? colors.brand[400] : colors.brand[500])
                  : (compact ? colors.gray[300] : colors.gray[400]);
              const strokeW = (compact ? 2.5 : 2) + (isSelected ? 1.5 : 0);
              return (
                <g key={`route-${route.routeKey || route.routeId || idx}`}>
                  {typeof onSelectRoute === 'function' && (
                    <Line
                      from={[route.from.lng, route.from.lat]}
                      to={[route.to.lng, route.to.lat]}
                      stroke="transparent"
                      strokeWidth={14}
                      strokeLinecap="round"
                      style={{ cursor: 'pointer', pointerEvents: 'stroke' }}
                      onClick={() => onSelectRoute(route.companyId, route.routeId)}
                    />
                  )}
                  <Line
                    from={[route.from.lng, route.from.lat]}
                    to={[route.to.lng, route.to.lat]}
                    stroke={strokeColor}
                    strokeWidth={strokeW}
                    strokeLinecap="round"
                    style={{ pointerEvents: 'none' }}
                  />
                </g>
              );
            })}

            {/* Company Location Markers - risk highlighted with color; clickable when onSelectCompanyLocation provided */}
            {companyLocations.map((location, idx) => {
              const isHovered = hoveredMarker === idx;
              const isSelected = selectedCompanyId === location.companyId;
              const riskColor = getMarkerColor(location.riskScore);
              const r = (isSelected ? (compact ? 11 : 10) : isHovered ? (compact ? 9 : 8) : (compact ? 7 : 6));
              return (
                <Marker
                  key={`marker-${location.companyId}-${idx}`}
                  coordinates={[location.coords.lng, location.coords.lat]}
                  onMouseEnter={() => setHoveredMarker(idx)}
                  onMouseLeave={() => setHoveredMarker(null)}
                  onClick={typeof onSelectCompanyLocation === 'function' ? () => onSelectCompanyLocation(location.companyId) : undefined}
                >
                  <circle
                    r={r}
                    fill={riskColor}
                    stroke={isSelected ? colors.warning[500] : colors.text.primary}
                    strokeWidth={isSelected ? 3 : (compact ? 2.5 : 2)}
                    style={{
                      cursor: typeof onSelectCompanyLocation === 'function' ? 'pointer' : 'default',
                      transition: 'all 0.2s'
                    }}
                  />
                  {(isHovered || isSelected) && (
                    <text
                      textAnchor="middle"
                      y={-12}
                      style={{
                        fontFamily: 'system-ui',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        fill: colors.text.primary,
                        pointerEvents: 'none'
                      }}
                    >
                      {location.companyId}
                    </text>
                  )}
                </Marker>
              );
            })}
          </ZoomableGroup>
        </ComposableMap>

        {/* Hover Tooltip */}
        {hoveredCountry && (
          <div 
            className="absolute top-4 right-4 p-3 rounded-lg shadow-lg max-w-xs"
            style={{ 
              backgroundColor: colors.background.card,
              border: `1px solid ${colors.border.light}`
            }}
          >
            <div className="font-semibold text-sm mb-1" style={{ color: colors.text.primary }}>
              {hoveredCountry}
            </div>
            {(() => {
              const sentiment = getSentimentForCountry(hoveredCountry);
              const companies = getCompaniesInCountry(hoveredCountry);
              
              return (
                <>
                  {sentiment && (
                    <div className="text-xs mb-2" style={{ color: colors.text.secondary }}>
                      {t('worldMapSentimentLabel')}{' '}
                      <span
                        style={{ color: getSentimentColor(sentiment.Tone), fontWeight: 'bold' }}
                      >
                        {sentiment.Tone > 0 ? '+' : ''}
                        {sentiment.Tone.toFixed(2)}
                      </span>
                    </div>
                  )}
                  {companies.length > 0 && (
                    <div className="text-xs" style={{ color: colors.text.secondary }}>
                      <Building2 className="h-3 w-3 inline mr-1" />
                      {t('worldMapCompaniesInCountry', { count: companies.length })}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* Hover Marker Tooltip */}
        {hoveredMarker !== null && companyLocations[hoveredMarker] && (
          <div 
            className="absolute bottom-4 left-4 p-3 rounded-lg shadow-lg"
            style={{ 
              backgroundColor: colors.background.card,
              border: `1px solid ${colors.border.light}`
            }}
          >
            {(() => {
              const location = companyLocations[hoveredMarker];
              return (
                <>
                  <div className="font-semibold text-sm mb-1" style={{ color: colors.text.primary }}>
                    {location.companyId}
                  </div>
                  <div className="text-xs mb-1" style={{ color: colors.text.secondary }}>
                    {location.address.getShortAddress?.() || location.address.country}
                  </div>
                  <div className="flex items-center space-x-2 text-xs">
                    <span style={{ color: colors.text.secondary }}>{t('worldMapRiskLabel')}</span>
                    <span 
                      className="font-bold"
                      style={{ color: getMarkerColor(location.riskScore) }}
                    >
                      {t('worldMapRiskScore', { score: location.riskScore.toFixed(0) })}
                    </span>
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </div>

      {/* Selected Country Detail Modal */}
      {selectedCountry && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedCountry(null)}
        >
          <div 
            className="bg-background-card border border-border-light rounded-lg shadow-elevation-5 max-w-2xl w-full max-h-[80vh] overflow-y-auto text-text-primary"
            onClick={(e) => e.stopPropagation()}
          >
            <div 
              className="p-6 border-b"
              style={{ borderColor: colors.border.light }}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-2xl font-bold mb-2" style={{ color: colors.text.primary }}>
                    {selectedCountry.Country}
                  </h3>
                  {selectedCountry.Tone !== undefined && (
                    <div className="flex items-center space-x-3">
                      {selectedCountry.Tone > 2 ? (
                        <TrendingUp className="h-5 w-5" style={{ color: getSentimentColor(selectedCountry.Tone) }} />
                      ) : selectedCountry.Tone < -2 ? (
                        <TrendingDown className="h-5 w-5" style={{ color: getSentimentColor(selectedCountry.Tone) }} />
                      ) : (
                        <Minus className="h-5 w-5" style={{ color: getSentimentColor(selectedCountry.Tone) }} />
                      )}
                      <span 
                        className="text-lg font-bold"
                        style={{ color: getSentimentColor(selectedCountry.Tone) }}
                      >
                        Tone: {selectedCountry.Tone > 0 ? '+' : ''}{selectedCountry.Tone.toFixed(2)}
                      </span>
                      <span 
                        className="px-3 py-1 rounded-full text-sm font-medium"
                        style={{ 
                          backgroundColor: `${getSentimentColor(selectedCountry.Tone)}20`,
                          color: getSentimentColor(selectedCountry.Tone)
                        }}
                      >
                        {selectedCountry.SentimentLabel}
                      </span>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setSelectedCountry(null)}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <span className="text-2xl" style={{ color: colors.text.secondary }}>×</span>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* Sentiment Metrics */}
              {selectedCountry.Tone !== undefined && (
                <div className="grid grid-cols-2 gap-4">
                  <div 
                    className="p-4 rounded-lg"
                    style={{ backgroundColor: colors.gray[50] }}
                  >
                    <p className="text-sm font-medium mb-1" style={{ color: colors.text.secondary }}>
                      Polarity
                    </p>
                    <p className="text-2xl font-bold" style={{ color: colors.text.primary }}>
                      {selectedCountry.Polarity?.toFixed(2) || 'N/A'}
                    </p>
                  </div>
                  <div 
                    className="p-4 rounded-lg"
                    style={{ backgroundColor: colors.gray[50] }}
                  >
                    <p className="text-sm font-medium mb-1" style={{ color: colors.text.secondary }}>
                      Activity Density
                    </p>
                    <p className="text-2xl font-bold" style={{ color: colors.text.primary }}>
                      {selectedCountry.ActivityDensity?.toFixed(2) || 'N/A'}
                    </p>
                  </div>
                </div>
              )}

              {/* Companies in this country */}
              {(() => {
                const companies = getCompaniesInCountry(selectedCountry.Country);
                if (companies.length > 0) {
                  return (
                    <div>
                      <h4 className="font-semibold mb-3" style={{ color: colors.text.primary }}>
                        Your Companies in {selectedCountry.Country} ({companies.length})
                      </h4>
                      <div className="space-y-2">
                        {companies.map((company, idx) => (
                          <div 
                            key={idx}
                            className="p-3 rounded-lg border flex items-center justify-between"
                            style={{ 
                              borderColor: colors.border.light,
                              backgroundColor: colors.background.card
                            }}
                          >
                            <div className="flex items-center space-x-3">
                              <MapPin 
                                className="h-5 w-5"
                                style={{ color: getMarkerColor(company.riskScore) }}
                              />
                              <div>
                                <p className="font-semibold text-sm" style={{ color: colors.text.primary }}>
                                  {company.companyId}
                                </p>
                                <p className="text-xs" style={{ color: colors.text.secondary }}>
                                  {company.address.getShortAddress?.() || company.address.country}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-xs" style={{ color: colors.text.secondary }}>Risk Score</p>
                              <p 
                                className="text-sm font-bold"
                                style={{ color: getMarkerColor(company.riskScore) }}
                              >
                                {company.riskScore.toFixed(0)}/100
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Last Updated */}
              {selectedCountry.timestamp && (
                <div 
                  className="pt-4 border-t text-sm"
                  style={{ borderColor: colors.border.light, color: colors.text.secondary }}
                >
                  <p>Last updated: {new Date(selectedCountry.timestamp).toLocaleString()}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {!compact && (
        <>
      {/* Instructions */}
      <div 
        className="p-3 rounded-lg text-xs"
        style={{ backgroundColor: colors.info[50], color: colors.info[700] }}
      >
        <Info className="h-3 w-3 inline mr-2" />
        Click on countries to see details. Hover over markers to see company info. Use mouse wheel to zoom.
      </div>

      {/* Last Updated Info */}
      {lastUpdated && (
        <div className="text-center">
          <p className="text-sm" style={{ color: colors.text.tertiary }}>
            Last updated: {lastUpdated.toLocaleString()}
          </p>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default InteractiveWorldMap;

